import { useRouter } from 'next/router';
import SEO from '@seo/index';
import { fetchUserProfileData } from '@api/fetchers/profile';
import EducatorPractices from '@cont/EducatorProfile/Practices/EducatorPractices';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { fetchPracticesWithFilters } from '@api/fetchers/practice';
import { SWRConfig } from 'swr';
import Layout from '@layout/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe, getIsLoggedIn } from '@api/selectors/auth';
import Dynamic from '@base/Dynamic/Dynamic';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';

const DppFinishSummary = Dynamic(() =>
  import('@cont/Practice/Listing/DppFinishSummary')
);

const Practices = ({
  profileInfo,
  practicesByFilters: fallbackPractices,
  filtersAppliedOnServer
}) => {
  const router = useRouter();

  let { authorID } = router.query;
  if (!authorID.startsWith('@')) authorID = `@${authorID}`;

  const { firstName, lastName, description } = profileInfo;
  const title = `${firstName} ${lastName} practices | Unacademy`;
  const url = `https://unacademy.com/${authorID}`;

  const meData = useMyInfo(getMe) || {};
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { showSummary } = useFinishSummary();

  return (
    <>
      <SEO
        title={title}
        url={url}
        description={description}
        openGraph={{ title, description, url }}
      />

      <SWRConfig value={{ provider: () => new Map() }}>
        {/* Adding provider creates a seperate cache for swr hooks used inside the
      SWR config */}
        <EducatorPractices
          authorID={authorID}
          profileInfo={profileInfo}
          fallbackPractices={fallbackPractices}
          meData={meData}
          isLoggedIn={isLoggedIn}
          filtersAppliedOnServer={filtersAppliedOnServer}
        />
      </SWRConfig>
      {showSummary && <DppFinishSummary source="Educator Profile" />}
    </>
  );
};
Practices.pageInfo = {
  layoutOptions: {
    hideFooter: true,
    fullHeightContent: true
  },
  isMWebSupported: true
};

export const getStaticProps = async ({ params: query }) => {
  try {
    let userName = query.authorID;
    if (!userName) return { notFound: true };
    if (userName.startsWith('@')) userName = query.authorID?.slice(1);

    const profileInfo = await fetchUserProfileData(userName);
    if (!profileInfo || profileInfo.errorCode)
      return {
        notFound: true,
        revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
      };

    const { isEducator, isExEducator, isModerator } = profileInfo;
    const isEducatorProfile = isEducator || isExEducator || isModerator;

    if (!isEducatorProfile)
      return {
        notFound: true,
        revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
      };

    const filtersToApply = {
      goal: { id: '' },
      topic: { id: '' },
      filter_by: { id: 'dpp' }
    };

    const practicesByFilters = await fetchPracticesWithFilters(
      userName,
      filtersToApply
    );

    return {
      props: {
        profileInfo,
        practicesByFilters,
        filtersAppliedOnServer: filtersToApply
      },
      revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
    };
  } catch {
    return { notFound: true, revalidate: 5 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default Practices;

Practices.Layout = Layout;
Practices.Wrapper = PageReduxProvider;
